import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Checkbox } from '../../../components/ui/Checkbox';

const ProfessionalInfoTab = ({ userRole, formData, onChange, onSave, isSaving, onAddSkill, onRemoveSkill, onAddCertification, onRemoveCertification, onUploadPortfolio }) => {
  const skillOptions = [
    { value: 'electrical', label: 'Elektriciteit' },
    { value: 'hvac', label: 'HVAC Systemen' },
    { value: 'plumbing', label: 'Loodgieterij' },
    { value: 'solar', label: 'Zonnepanelen' },
    { value: 'heat_pump', label: 'Warmtepompen' },
    { value: 'airco', label: 'Airconditioning' },
    { value: 'ventilation', label: 'Ventilatie' },
    { value: 'smart_home', label: 'Smart Home Systemen' }
  ];

  const experienceLevelOptions = [
    { value: 'junior', label: 'Junior (0-2 jaar)' },
    { value: 'medior', label: 'Medior (2-5 jaar)' },
    { value: 'senior', label: 'Senior (5-10 jaar)' },
    { value: 'expert', label: 'Expert (10+ jaar)' }
  ];

  const availabilityOptions = [
    { value: 'full_time', label: 'Fulltime' },
    { value: 'part_time', label: 'Parttime' },
    { value: 'weekends', label: 'Weekenden' },
    { value: 'flexible', label: 'Flexibel' }
  ];

  const serviceTypeOptions = [
    { value: 'residential', label: 'Woningbouw' },
    { value: 'commercial', label: 'Commercieel' },
    { value: 'industrial', label: 'Industrieel' },
    { value: 'maintenance', label: 'Onderhoud' }
  ];

  const networkTypeOptions = [
    { value: 'recruitment', label: 'Recruitment Bureau' },
    { value: 'trade_org', label: 'Branche Organisatie' },
    { value: 'training', label: 'Opleidingsinstituut' },
    { value: 'business_dev', label: 'Business Development' }
  ];

  if (userRole === 'contractor') {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
            Professionele Informatie
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            Beheer uw vaardigheden, certificeringen en beschikbaarheid
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Ervaringsniveau"
            options={experienceLevelOptions}
            value={formData?.experienceLevel}
            onChange={(value) => onChange('experienceLevel', value)}
            required
          />

          <Select
            label="Beschikbaarheid"
            options={availabilityOptions}
            value={formData?.availability}
            onChange={(value) => onChange('availability', value)}
            multiple
            searchable
            required
          />

          <Input
            label="Uurtarief (€)"
            type="number"
            placeholder="45.00"
            value={formData?.hourlyRate}
            onChange={(e) => onChange('hourlyRate', e?.target?.value)}
            description="Uw standaard uurtarief"
          />

          <Input
            label="Reisafstand (km)"
            type="number"
            placeholder="50"
            value={formData?.travelDistance}
            onChange={(e) => onChange('travelDistance', e?.target?.value)}
            description="Maximale reisafstand voor opdrachten"
          />
        </div>
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg md:text-xl font-medium text-foreground">
              Vaardigheden
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddSkill}
              iconName="Plus"
              iconPosition="left"
            >
              Vaardigheid toevoegen
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData?.skills && formData?.skills?.length > 0 ? (
              formData?.skills?.map((skill, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-lg"
                >
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm font-medium">{skill?.label}</span>
                  <button
                    onClick={() => onRemoveSkill(index)}
                    className="hover:text-error transition-smooth"
                    aria-label="Verwijder vaardigheid"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                Nog geen vaardigheden toegevoegd
              </p>
            )}
          </div>
        </div>
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg md:text-xl font-medium text-foreground">
              Certificeringen
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCertification}
              iconName="Plus"
              iconPosition="left"
            >
              Certificering toevoegen
            </Button>
          </div>

          <div className="space-y-3">
            {formData?.certifications && formData?.certifications?.length > 0 ? (
              formData?.certifications?.map((cert, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-muted rounded-lg"
                >
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Award" size={20} color="var(--color-success)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-foreground mb-1">
                      {cert?.name}
                    </h5>
                    <p className="caption text-muted-foreground mb-1">
                      {cert?.issuer} • Geldig tot: {cert?.expiryDate}
                    </p>
                    <p className="caption text-muted-foreground">
                      Certificaatnummer: {cert?.number}
                    </p>
                  </div>
                  <button
                    onClick={() => onRemoveCertification(index)}
                    className="text-muted-foreground hover:text-error transition-smooth"
                    aria-label="Verwijder certificering"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                Nog geen certificeringen toegevoegd
              </p>
            )}
          </div>
        </div>
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg md:text-xl font-medium text-foreground">
              Portfolio
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={onUploadPortfolio}
              iconName="Upload"
              iconPosition="left"
            >
              Foto uploaden
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {formData?.portfolio && formData?.portfolio?.length > 0 ? (
              formData?.portfolio?.map((item, index) => (
                <div key={index} className="relative group aspect-square rounded-lg overflow-hidden">
                  <Image
                    src={item?.image}
                    alt={item?.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-smooth flex items-center justify-center">
                    <button
                      className="w-10 h-10 bg-error text-error-foreground rounded-full flex items-center justify-center elevation-md"
                      aria-label="Verwijder foto"
                    >
                      <Icon name="Trash2" size={18} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 md:col-span-3 lg:col-span-4 text-center py-8">
                <Icon name="Image" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Nog geen portfolio foto's toegevoegd
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <Button
            variant="default"
            onClick={onSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
          >
            Wijzigingen opslaan
          </Button>
        </div>
      </div>
    );
  }

  if (userRole === 'client') {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
            Bedrijfsinformatie
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            Beheer uw bedrijfsgegevens en voorkeuren
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Type dienstverlening"
            options={serviceTypeOptions}
            value={formData?.serviceTypes}
            onChange={(value) => onChange('serviceTypes', value)}
            multiple
            searchable
            required
          />

          <Input
            label="Aantal medewerkers"
            type="number"
            placeholder="10"
            value={formData?.employeeCount}
            onChange={(e) => onChange('employeeCount', e?.target?.value)}
          />

          <div className="md:col-span-2">
            <Input
              label="Bedrijfsomschrijving"
              type="text"
              placeholder="Korte beschrijving van uw bedrijf"
              value={formData?.companyDescription}
              onChange={(e) => onChange('companyDescription', e?.target?.value)}
            />
          </div>
        </div>
        <div className="border-t border-border pt-6">
          <h4 className="text-lg md:text-xl font-medium text-foreground mb-4">
            Facturatiegegevens
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="Facturatieadres"
              type="text"
              placeholder="Straat en huisnummer"
              value={formData?.billingAddress}
              onChange={(e) => onChange('billingAddress', e?.target?.value)}
              required
            />

            <Input
              label="IBAN"
              type="text"
              placeholder="NL00 BANK 0123 4567 89"
              value={formData?.iban}
              onChange={(e) => onChange('iban', e?.target?.value)}
            />

            <Input
              label="Betalingstermijn (dagen)"
              type="number"
              placeholder="30"
              value={formData?.paymentTerms}
              onChange={(e) => onChange('paymentTerms', e?.target?.value)}
            />

            <Input
              label="PO-nummer prefix"
              type="text"
              placeholder="PO-"
              value={formData?.poPrefix}
              onChange={(e) => onChange('poPrefix', e?.target?.value)}
              description="Prefix voor inkoopordernummers"
            />
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <Button
            variant="default"
            onClick={onSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
          >
            Wijzigingen opslaan
          </Button>
        </div>
      </div>
    );
  }

  if (userRole === 'affiliate') {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
            Affiliate Informatie
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            Beheer uw netwerk en commissievoorkeuren
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Netwerktype"
            options={networkTypeOptions}
            value={formData?.networkType}
            onChange={(value) => onChange('networkType', value)}
            required
          />

          <Input
            label="Aantal actieve contacten"
            type="number"
            placeholder="50"
            value={formData?.activeContacts}
            onChange={(e) => onChange('activeContacts', e?.target?.value)}
          />

          <div className="md:col-span-2">
            <Input
              label="Specialisatie"
              type="text"
              placeholder="Bijv. HVAC professionals, Elektriciens"
              value={formData?.specialization}
              onChange={(e) => onChange('specialization', e?.target?.value)}
            />
          </div>
        </div>
        <div className="border-t border-border pt-6">
          <h4 className="text-lg md:text-xl font-medium text-foreground mb-4">
            Commissievoorkeuren
          </h4>

          <div className="space-y-4">
            <Checkbox
              label="Automatische uitbetaling inschakelen"
              description="Commissies worden automatisch uitbetaald bij drempelbedrag"
              checked={formData?.autoPayoutEnabled}
              onChange={(e) => onChange('autoPayoutEnabled', e?.target?.checked)}
            />

            <Input
              label="Uitbetalingsdrempel (€)"
              type="number"
              placeholder="100.00"
              value={formData?.payoutThreshold}
              onChange={(e) => onChange('payoutThreshold', e?.target?.value)}
              description="Minimum bedrag voor automatische uitbetaling"
            />

            <Input
              label="IBAN voor uitbetalingen"
              type="text"
              placeholder="NL00 BANK 0123 4567 89"
              value={formData?.payoutIban}
              onChange={(e) => onChange('payoutIban', e?.target?.value)}
              required
            />
          </div>
        </div>
        <div className="border-t border-border pt-6">
          <h4 className="text-lg md:text-xl font-medium text-foreground mb-4">
            Referral Link
          </h4>

          <div className="flex gap-2">
            <Input
              type="text"
              value={formData?.referralLink}
              readOnly
              className="flex-1"
            />
            <Button
              variant="outline"
              iconName="Copy"
              onClick={() => navigator.clipboard?.writeText(formData?.referralLink)}
            >
              Kopiëren
            </Button>
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <Button
            variant="default"
            onClick={onSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
          >
            Wijzigingen opslaan
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
          Professionele Informatie
        </h3>
        <p className="text-sm md:text-base text-muted-foreground mb-6">
          Geen aanvullende professionele informatie vereist voor deze rol
        </p>
      </div>

      <div className="flex justify-end pt-4">
        <Button
          variant="default"
          onClick={onSave}
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
        >
          Wijzigingen opslaan
        </Button>
      </div>
    </div>
  );
};

export default ProfessionalInfoTab;